import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { FaLayerGroup } from 'react-icons/fa';
import { fetchFloorVisualization } from './propertyApi';
import { HIGH_DEMAND_THRESHOLD } from './propertyRecordUtils';
import UnitDetailPanel from './UnitDetailPanel';

const VIZ_LABELS = {
  available: 'Available',
  occupied: 'Occupied',
  highDemand: 'High Demand',
};

function UnitCell({ unit, selected, onSelect }) {
  const title = [
    unit.unitNumber,
    unit.unitType,
    unit.rentLabel,
    VIZ_LABELS[unit.vizStatus],
    unit.openInterestCount ? `${unit.openInterestCount} interested` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      type='button'
      className={`re-unit-cell re-unit-cell--${unit.vizStatus}${selected ? ' selected' : ''}`}
      title={title}
      onClick={() => onSelect(unit.id)}
    >
      <span className='re-unit-cell__dot' aria-hidden='true' />
      <span className='re-unit-cell__number'>{unit.unitNumber}</span>
      {unit.rentLabel && <span className='re-unit-cell__rent'>{unit.rentLabel}</span>}
    </button>
  );
}

function PropertyFloorsTab({ property, selectedUnitId, onSelectUnit }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [floors, setFloors] = useState([]);
  const [stats, setStats] = useState({ available: 0, occupied: 0, highDemand: 0 });
  const abortRef = useRef(null);

  useEffect(() => {
    if (!property?.id) {
      setFloors([]);
      setLoading(false);
      return undefined;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    fetchFloorVisualization(property.id, controller.signal)
      .then(result => {
        if (controller.signal.aborted) {
          return;
        }
        setFloors(result.floors || []);
        setStats(result.stats || { available: 0, occupied: 0, highDemand: 0 });
      })
      .catch(err => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') {
          return;
        }
        console.error('Floor visualization load failed:', err);
        setError(err?.message || 'Failed to load floors');
        setFloors([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [property?.id]);

  const selectedUnit = useMemo(() => {
    for (const floor of floors) {
      const match = floor.units.find(unit => unit.id === selectedUnitId);
      if (match) {
        return match;
      }
    }
    return null;
  }, [floors, selectedUnitId]);

  if (loading) {
    return (
      <div className='re-floors-placeholder'>
        <Spinner animation='border' role='status' />
        <p className='mt-3 mb-0'>Loading floor visualization…</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant='danger' className='m-3'>
        {error}
      </Alert>
    );
  }

  if (floors.length === 0) {
    return (
      <div className='re-floors-placeholder'>
        <div className='re-floors-placeholder__icon'>
          <FaLayerGroup />
        </div>
        <h3>No floors or units</h3>
        <p>
          {property?.name || 'This property'} does not have floor or unit inventory yet.
        </p>
      </div>
    );
  }

  return (
    <div className='re-floor-viz'>
      <div className='re-floor-viz__header'>
        <div>
          <h3 className='re-floor-viz__title'>Floor visualization</h3>
          <p className='re-floor-viz__subtitle'>{property?.name}</p>
        </div>
        <div className='re-floor-viz__legend' aria-label='Unit status legend'>
          <span>
            <i className='re-dot re-dot--available' /> Available
            <em>{stats.available}</em>
          </span>
          <span>
            <i className='re-dot re-dot--occupied' /> Occupied
            <em>{stats.occupied}</em>
          </span>
          <span>
            <i className='re-dot re-dot--demand' /> High Demand
            <em>{stats.highDemand}</em>
          </span>
        </div>
      </div>

      <div className='re-floor-viz__split'>
        <div className='re-building'>
          <div className='re-building__roof' aria-hidden='true' />
          <div className='re-building__stack'>
            {floors.map(floor => (
              <div key={floor.id} className='re-building__floor'>
                <div className='re-building__floor-label' title={floor.name || floor.label}>
                  {floor.label}
                </div>
                <div className='re-building__units'>
                  {floor.units.length === 0 ? (
                    <div className='re-building__empty-floor'>No units</div>
                  ) : (
                    floor.units.map(unit => (
                      <UnitCell
                        key={unit.id}
                        unit={unit}
                        selected={unit.id === selectedUnitId}
                        onSelect={onSelectUnit}
                      />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className='re-building__base' aria-hidden='true' />
          <p className='re-floor-viz__hint'>
            High Demand is derived: Available units with {HIGH_DEMAND_THRESHOLD}+ open
            interested leads. Click a unit for demand and hot leads.
          </p>
        </div>

        <UnitDetailPanel key={selectedUnit?.id || 'none'} unit={selectedUnit} />
      </div>
    </div>
  );
}

export default PropertyFloorsTab;
